// Newsletter.js
import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import '../styles/newsletter.css';

const Newsletter = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: ''
    });
    const [status, setStatus] = useState({
        message: '',
        isError: false,
        isSubmitting: false
    });

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Basic validation
        if (!formData.name.trim() || !formData.email.trim()) {
            setStatus({
                message: 'Please fill in all fields',
                isError: true,
                isSubmitting: false
            });
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setStatus({
                message: 'Please enter a valid email address',
                isError: true,
                isSubmitting: false
            });
            return;
        }

        setStatus({
            message: '',
            isError: false,
            isSubmitting: true
        });

        try {
            const { error } = await supabase
                .from('newsletter_subscribers')
                .insert([
                    {
                        name: formData.name.trim(),
                        email: formData.email.toLowerCase().trim()
                    }
                ]);

            if (error) {
                if (error.code === '23505') { // Unique violation error code
                    setStatus({
                        message: 'You are already subscribed to our newsletter!',
                        isError: true,
                        isSubmitting: false
                    });
                } else {
                    throw error;
                }
            } else {
                // Fires only on a genuinely new subscriber - the duplicate-email
                // branch above returns before this point, so the count is not
                // inflated by people resubmitting.
                trackEvent('newsletter-signup');
                setStatus({
                    message: 'Thank you for subscribing to our newsletter!',
                    isError: false,
                    isSubmitting: false
                });
                setFormData({ name: '', email: '' }); // Clear form
            }
        } catch (error) {
            console.error('Error:', error);
            setStatus({
                message: 'Something went wrong. Please try again later.',
                isError: true,
                isSubmitting: false
            });
        }
    };

    return (
        <section className="newsletter-section" data-aos="fade-up">
            <div className="newsletter-content">
                <div className="newsletter-text">
                    <h2 className="newsletter-header">Join Our Sleep Experts' Newsletter</h2>
                    <p className="newsletter-description">
                        Sign up to receive the latest tips, techniques, and insights to help you sleep better.
                        Get exclusive advice, product recommendations, and tools to make every night a restful one.
                    </p>
                </div>
                <div className="newsletter-form">
                    <form onSubmit={handleSubmit}>
                        <input 
                            type="text" 
                            name="name" 
                            placeholder="Your Name" 
                            className="form-input"
                            value={formData.name}
                            onChange={handleChange}
                            disabled={status.isSubmitting}
                        />
                        <input 
                            type="email" 
                            name="email" 
                            placeholder="Your Email" 
                            className="form-input"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={status.isSubmitting}
                        />
                        <button 
                            type="submit" 
                            className="form-button"
                            disabled={status.isSubmitting}
                        >
                            {status.isSubmitting ? 'Subscribing...' : 'Subscribe'}
                        </button>
                    </form>
                    {status.message && (
                        <div className={`form-message ${status.isError ? 'error' : 'success'}`}>
                            {status.message}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Newsletter;
